/**
 * UsageReportView — the top-level Usage surface (/usage).
 *
 * One page, two shapes, decided by the workspace's contract
 * (`planFeatures.billingContract` on the session): `metered` is the rate-card
 * report every workspace bills on today (MeteredUsageView), `pr_plan` is the
 * monthly bucket of merged pull requests YAGNI authored
 * (PullRequestPlanView). There is no switch for customers; a super admin in
 * god mode gets a small one to preview the other shape.
 *
 * ADR-0028: sell-side numbers only, admin-gated server-side. A member gets
 * their own line, never a spinner that dies.
 */
import { useState, type ReactNode } from "react";
import type { BillingContract, YagniCodeUsageDeveloperRow } from "@yagni/shared";

import "./usage.css";
import { ListHead } from "../queue/index.js";
import { useAuthSession, useCanViewUsage } from "../hooks/useWorkspaceRole.js";
import { resolveBillingContract } from "../hooks/useShowsRunCost.js";
import { MeteredUsageView } from "./MeteredUsageView.js";
import { PullRequestPlanView } from "./PullRequestPlanView.js";
import { PersonalTeamUsage } from "./PersonalTeamUsage.js";
import { laneLabel } from "./ledger.js";
import {
  monthLabel,
  summarizeRow,
  summarySavingsPercent,
  usd,
  usePersonalTeamUsage,
  usePersonalUsage,
} from "./usageReport.js";
import { Note, Sec, Seg, Tile, Tiles } from "./usageParts.js";
import { savedWithShare } from "./usageGlass.js";

/** The contract the session says this workspace bills under; absent reads as metered. */
export function useBillingContract(): BillingContract {
  return resolveBillingContract(useAuthSession());
}

const CONTRACT_OPTIONS = [
  { key: "metered", label: "Metered · rate card" },
  { key: "pr_plan", label: "Plan · pull requests" },
] as const;

/**
 * The organization report, in the shape the contract picks. `contract`
 * overrides the session's (tests, previews).
 */
export function UsageReportView({ contract }: { contract?: BillingContract } = {}) {
  const sessionContract = useBillingContract();
  const session = useAuthSession();
  const godMode = session?.godMode?.active === true;
  const [preview, setPreview] = useState<BillingContract | null>(null);
  const shape = contract ?? preview ?? sessionContract;

  const previewSwitch: ReactNode = godMode ? (
    <span title="God mode: preview the other contract's page. Customers never see this switch.">
      <Seg label="Preview a contract" options={CONTRACT_OPTIONS} value={shape} onChange={setPreview} big />
    </span>
  ) : null;

  return (
    <div data-testid="usage-report" data-contract={shape} style={{ display: "contents" }}>
      {shape === "pr_plan" ? (
        <PullRequestPlanView previewSwitch={previewSwitch} />
      ) : (
        <MeteredUsageView previewSwitch={previewSwitch} />
      )}
    </div>
  );
}

/**
 * The member view: your own line of the report (spend, the same tokens at
 * Claude list, savings, your lane split), then the Agents' spend on your own
 * personal Teams. Never anyone else's numbers.
 */
export function PersonalUsageView() {
  const { row, error, loading } = usePersonalUsage();
  // Read beside the line, not after it; a failure here hides only its block.
  const teams = usePersonalTeamUsage();
  const month = monthLabel(new Date());

  return (
    <div data-testid="usage-personal" style={{ display: "contents" }}>
      <ListHead
        title={`Usage · ${month}`}
        sub={["Your own line of the workspace report", "Workspace admins see everyone's"]}
      />
      {loading ? (
        <Note>Reading your line</Note>
      ) : error ? (
        <Note tone="honest">{error}</Note>
      ) : !row || row.totalDispatches === 0 ? (
        <div className="empty dashed">
          <p>No usage on your line yet this month.</p>
          <p>
            Once you work through YAGNI Code, your spend and what the same work would have cost at Claude list prices
            land here.
          </p>
        </div>
      ) : (
        <PersonalLine row={row} month={month} />
      )}
      {!loading && <PersonalTeamUsage state={teams} />}
    </div>
  );
}

function PersonalLine({ row, month }: { row: YagniCodeUsageDeveloperRow; month: string }) {
  const summary = summarizeRow(row);
  const percent = summarySavingsPercent(summary);
  return (
    <>
      <p className="brief">
        {percent === null
          ? `You billed ${usd(summary.billedMillicents)} across ${row.totalDispatches.toLocaleString("en-US")} dispatches in ${month} so far.`
          : `You billed ${usd(summary.billedMillicents)} in ${month} so far, against ${usd(summary.claudeMillicents)} the same work would have cost at Claude list prices. ${usd(summary.savedMillicents)} saved (${percent}%).`}
      </p>
      <Tiles>
        <Tile
          value={usd(summary.billedMillicents)}
          label="Billed at your lanes"
          sub={`${row.totalDispatches.toLocaleString("en-US")} dispatches`}
        />
        <Tile
          value={usd(summary.claudeMillicents)}
          label="At Claude list"
          tone="quiet"
          sub={`same tokens, ${usd(summary.promptWritingMillicents)} of it prompt writing`}
        />
        <Tile
          value={savedWithShare(summary.savedMillicents, percent, usd)}
          label="Saved vs Claude"
          tone="green"
          sub="routing + lane pricing"
        />
      </Tiles>
      {row.counterfactualIncomplete && (
        <Note>
          Some of your spend ran on a lane with no Claude list equivalent, so the comparison covers part of it rather
          than guess.
        </Note>
      )}
      <Sec k="01" title="By lane" fact="where the routing sent your work">
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Lane</th>
                <th className="num">Dispatches</th>
                <th className="num">Billed</th>
              </tr>
            </thead>
            <tbody>
              {row.lanes.map((lane) => (
                <tr key={lane.lane}>
                  <td>{laneLabel(lane.lane)}</td>
                  <td className="num">{lane.dispatches.toLocaleString("en-US")}</td>
                  <td className="num">{usd(lane.sellMillicents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Sec>
    </>
  );
}

/** Every Usage page (the report, a member's line, the drills) sits in one `.page` column. */
export function UsageColumn({ children }: { children: ReactNode }) {
  return <div className="page usage">{children}</div>;
}

/** Route wrapper. Admins (and a god-mode operator) get the organization report; members only ever their own line. */
export function UsageRoute() {
  const isAdmin = useCanViewUsage();
  return <UsageColumn>{isAdmin ? <UsageReportView /> : <PersonalUsageView />}</UsageColumn>;
}
